import { useCallback, useEffect, useState } from 'react';
import { formatMYR } from '@72on/domain';
import {
  getTrial,
  type TrialReport,
  getDaily,
  getProfit,
  getStockValuation,
  getShifts,
  type ShiftsReport,
  getTopItems,
  type DailyReport,
  type ProfitReport,
  type StockValuation,
  type TopItemsReport,
} from '../offline/admin.js';

import { API_BASE } from '../config.js';

function today(): string {
  return new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

interface Props {
  token: string;
  /** Tarikh mula percubaan 30 hari (YYYY-MM-DD), jika ditetapkan. */
  trialStart?: string | undefined;
}

/**
 * Laporan pemilik: harian, untung, item terlaris, nilai stok.
 *
 * Ini jawapan kepada soalan dalam BRIEF §8: "bolehkah pemilik menjawab berapa untung bulan ini
 * tanpa bertanya sesiapa?"
 */
export function ReportsPanel({ token, trialStart }: Props): React.ReactElement {
  const [date, setDate] = useState(today);
  const [from, setFrom] = useState(() => {
    const now = new Date(Date.now() + 8 * 60 * 60 * 1000);
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(today);

  const [daily, setDaily] = useState<DailyReport | null>(null);
  const [profit, setProfit] = useState<ProfitReport | null>(null);
  const [top, setTop] = useState<TopItemsReport | null>(null);
  const [valuation, setValuation] = useState<StockValuation | null>(null);
  const [trial, setTrial] = useState<TrialReport | null>(null);
  const [shifts, setShifts] = useState<ShiftsReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const [d, p, t, v, sh, tr] = await Promise.all([
        getDaily(API_BASE, token, date),
        getProfit(API_BASE, token, from, to),
        getTopItems(API_BASE, token, from, to),
        getStockValuation(API_BASE, token),
        getShifts(API_BASE, token, from, to),
        // '' bukan null: `??` tidak akan jatuh balik, jadi tarikh kosong mesti diperiksa terus.
        getTrial(API_BASE, token, trialStart !== undefined && trialStart !== '' ? trialStart : from, to),
      ]);
      setDaily(d);
      setProfit(p);
      setTop(t);
      setValuation(v);
      setShifts(sh);
      setTrial(tr);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'gagal memuatkan laporan');
    } finally {
      setBusy(false);
    }
  }, [token, date, from, to, trialStart]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="panel-reports">
      <h2>Laporan</h2>

      <div className="report-filters">
        <label>
          Tarikh harian
          <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
        </label>
        <label>
          Dari
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          Hingga
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <button type="button" disabled={busy} onClick={() => void load()}>
          {busy ? 'Memuat…' : 'Muat semula'}
        </button>
      </div>

      {error !== null && <p className="error">{error}</p>}

      {daily !== null && (
        <div className="report-grid">
          <div className="card">
            <h3>Harian — {daily.date}</h3>
            <dl className="totals">
              <dt>Bil</dt>
              <dd>{daily.sales_count}</dd>
              <dt>Jualan</dt>
              <dd>RM{formatMYR(daily.gross_sen)}</dd>
              <dt>Cukai</dt>
              <dd>RM{formatMYR(daily.tax_sen)}</dd>
              <dt>Kos barang</dt>
              <dd>RM{formatMYR(daily.cogs_sen)}</dd>
              <dt>Purata bakul</dt>
              <dd>RM{formatMYR(daily.avg_basket_sen)}</dd>
              <dt className="grand">Untung kasar</dt>
              <dd className="grand">RM{formatMYR(daily.gross_profit_sen)}</dd>
            </dl>
          </div>

          {profit !== null && (
            <div className="card">
              <h3>
                Untung {profit.from} → {profit.to}
              </h3>
              <dl className="totals">
                <dt>Bil</dt>
                <dd>{profit.sales_count}</dd>
                <dt>Hasil (tanpa cukai)</dt>
                <dd>RM{formatMYR(profit.revenue_sen)}</dd>
                <dt>Kos</dt>
                <dd>RM{formatMYR(profit.cogs_sen)}</dd>
                <dt>Margin</dt>
                <dd>{(profit.margin_bp / 100).toFixed(1)}%</dd>
                <dt className="grand">Untung kasar</dt>
                <dd className="grand">RM{formatMYR(profit.gross_profit_sen)}</dd>
              </dl>
              {profit.cost_unknown_lines > 0 && (
                <p className="muted">
                  {profit.cost_unknown_lines} baris tiada kos direkod — untung tidak termasuk baris itu.
                </p>
              )}
            </div>
          )}

          {valuation !== null && (
            <div className="card">
              <h3>Nilai stok</h3>
              <dl className="totals">
                <dt>Pada kos</dt>
                <dd>RM{formatMYR(valuation.cost_sen)}</dd>
                <dt>Pada harga jualan</dt>
                <dd>RM{formatMYR(valuation.retail_sen)}</dd>
                <dt>SKU berbaki</dt>
                <dd>{valuation.variant_count}</dd>
                <dt>Stok negatif</dt>
                <dd className={valuation.negative_count > 0 ? 'error' : ''}>
                  {valuation.negative_count}
                </dd>
              </dl>
            </div>
          )}
        </div>
      )}

      {top !== null && top.items.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Kuantiti</th>
              <th>Hasil</th>
              <th>Untung</th>
            </tr>
          </thead>
          <tbody>
            {top.items.map((item) => (
              <tr key={item.variant_id ?? item.name}>
                <td>{item.name}</td>
                <td>
                  {item.qty} {item.unit}
                </td>
                <td>RM{formatMYR(item.revenue_sen)}</td>
                <td>RM{formatMYR(item.profit_sen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {top !== null && top.items.length === 0 && (
        <p className="muted">Tiada jualan dalam tempoh ini.</p>
      )}
      {trial !== null && (
        <div className="card">
          <h3>Percubaan 30 hari</h3>
          {trialStart !== undefined && trialStart !== '' ? (
            <p className="muted">
              Bermula {trialStart} · hari ke-
              {Math.max(
                1,
                Math.floor(
                  (new Date(`${to}T00:00:00+08:00`).getTime() -
                    new Date(`${trialStart}T00:00:00+08:00`).getTime()) /
                    86_400_000,
                ) + 1,
              )}{' '}
              daripada 30
            </p>
          ) : (
            <p className="muted">
              Tarikh mula belum ditetapkan — angka di bawah ialah tempoh laporan ini. Tetapkan di
              Tetapan → Percubaan 30 hari.
            </p>
          )}
          <dl className="totals">
            <dt>Jualan</dt>
            <dd>
              {trial.sales.count} bil · RM{formatMYR(trial.sales.total_sen)}
            </dd>
            <dt>Hari ada jualan</dt>
            <dd>{trial.sales.days_with_sales}</dd>
            <dt>Beza tunai dalam ±RM1</dt>
            <dd>
              {trial.cash.closed_shifts === 0
                ? 'tiada syif tertutup'
                : `${trial.cash.within_one_ringgit}/${trial.cash.closed_shifts} (${
                    ((trial.cash.pass_rate_bp ?? 0) / 100).toFixed(0)
                  }%)`}
            </dd>
            <dt>p95 lag sync</dt>
            <dd>
              {trial.sync.p95_seconds === null
                ? 'tiada data'
                : `${trial.sync.p95_seconds.toFixed(1)} saat`}
            </dd>
            <dt>p95 masa tutup syif</dt>
            <dd>
              {trial.shifts.p95_close_minutes === null
                ? 'tiada data'
                : `${trial.shifts.p95_close_minutes.toFixed(1)} minit`}
            </dd>
            <dt>Liputan kos</dt>
            <dd>
              {trial.cost.coverage_bp === null
                ? 'tiada varian'
                : `${(trial.cost.coverage_bp / 100).toFixed(0)}% (${
                    trial.cost.variants_without_cost
                  } varian tanpa kos)`}
            </dd>
            <dt>Jualan perlu semakan</dt>
            <dd>{trial.sales.needs_review}</dd>
          </dl>
          <p className="muted">
            Empat kriteria lain tidak boleh dikira daripada data dan mesti disemak manual: semua
            jualan masuk sistem, resit tercetak pada percubaan pertama, stok fizikal dalam ±2%, dan
            masa tutup syif ≤10 minit.
          </p>
        </div>
      )}

      {shifts !== null && (
        <div className="card">
          <h3>Syif kasir</h3>
          <p className="muted">
            Setiap syif ialah satu tempoh tanggungjawab ke atas laci wang. Beza tunai yang bukan sifar
            bermakna kiraan fizikal tidak sepadan dengan jualan.
          </p>
          <table className="table table-shifts">
            <thead>
              <tr>
                <th>Dibuka</th>
                <th>Kasir</th>
                <th>Peranti</th>
                <th>Tunai awal</th>
                <th>Sepatutnya</th>
                <th>Dikira</th>
                <th>Beza</th>
                <th>Jualan</th>
              </tr>
            </thead>
            <tbody>
              {shifts.data.map((shift) => (
                <tr key={shift.id}>
                  <td>{new Date(shift.opened_at).toLocaleString("ms-MY")}</td>
                  <td>{shift.cashier_name ?? "(tanpa kasir)"}</td>
                  <td>
                    #{shift.device_no} · {shift.outlet_code}
                  </td>
                  <td>RM{formatMYR(shift.opening_cash_sen)}</td>
                  <td>
                    {shift.expected_cash_sen === null ? "—" : `RM${formatMYR(shift.expected_cash_sen)}`}
                  </td>
                  <td>
                    {shift.closing_cash_sen === null
                      ? "belum ditutup"
                      : `RM${formatMYR(shift.closing_cash_sen)}`}
                  </td>
                  <td className={shift.variance_sen === null || shift.variance_sen === 0 ? "" : "variance"}>
                    {shift.variance_sen === null ? "—" : `RM${formatMYR(shift.variance_sen)}`}
                  </td>
                  <td>
                    {shift.sales_count} bil · RM{formatMYR(shift.sales_total_sen)}
                  </td>
                </tr>
              ))}
              {shifts.data.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted">
                    Tiada syif dalam tempoh ini.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

    </section>
  );
}
